import { Spinner } from "./";
import React, { useEffect, useRef, useState } from "react";
import { Mixpanel, MxE } from "../utils/mixpanelWrapper";

interface VideoProps {
  src: string;
  unloader?: JSX.Element;
  spinnerSize?: number;
  className?: string;
  backupSrcs?: string[];
  [passthrough: string]: any;
}

export const Video = ({
  src,
  unloader = <span />,
  spinnerSize = 20,
  className = "",
  alt = "",
  backupSrcs = [],
  ...props
}: VideoProps): JSX.Element => {
  const [currentAttemptIndex, setCurrentAttemptIndex] = useState(0);
  const [loadingState, setLoadingState] = useState("loading");
  const [videoSrc, setVideoSrc] = useState<string>();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (src) {
      setVideoSrc(undefined);
      setLoadingState("loading");
      setCurrentAttemptIndex(0);
    }
  }, [src]);

  useEffect(() => {
    if (!videoRef.current) {
      videoRef.current = document.createElement("video");
    }
  }, [videoRef]);

  useEffect(() => {
    if (videoSrc) {
      const videoPlayer = document.getElementById("video") as HTMLVideoElement;
      if (videoPlayer) {
        if (props.currentTime || props.currentTime === 0) {
          videoPlayer.currentTime = props.currentTime;
        }
      }
    }
  }, [props, videoSrc]);

  useEffect(() => {
    let isMounted = true;
    const allSources = [src, ...backupSrcs];
    if (currentAttemptIndex >= allSources.length) {
      if (isMounted) {
        setLoadingState("error");
      }
      return;
    }
    const currentSrc = allSources[currentAttemptIndex];
    if (!videoRef.current) {
      videoRef.current = document.createElement("video");
    }
    const loader = videoRef.current;
    loader.src = currentSrc;
    const handleCanPlayThrough = () => {
      if (isMounted) {
        setVideoSrc(currentSrc);
        setLoadingState("loaded");
      }
    };
    const handleError = () => {
      if (isMounted) {
        setCurrentAttemptIndex(prev => prev + 1);
        setLoadingState("loading");
      }
    };
    loader.addEventListener("canplaythrough", handleCanPlayThrough);
    loader.addEventListener("error", handleError);
    loader.load();
    return () => {
      isMounted = false;
      loader.removeEventListener("canplaythrough", handleCanPlayThrough);
      loader.removeEventListener("error", handleError);
    };
  }, [src, backupSrcs, currentAttemptIndex]);

  switch (loadingState) {
    case "loaded":
      return (
        <video
          {...props}
          className={className}
          controls
          id="video"
          preload="metadata"
          src={videoSrc}
          onPlay={() => {
            Mixpanel.track(MxE.HOWTO_VIDEO_STARTED, { fileName: videoSrc });
          }}
          onEnded={() => {
            Mixpanel.track(MxE.HOWTO_VIDEO_COMPLETED, { fileName: videoSrc });
          }}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      );
    case "loading":
      return <Spinner size={spinnerSize} />;
    case "error":
      return unloader;
    default:
      return <span />;
  }
};

export default Video;
